import React, { useCallback, useEffect } from 'react';
import { useSidebar } from '@atlassian/jira-business-app-wrapper/src/controllers/sidebar/index.tsx';
import { SELECTED_COMMENT_PARAM } from '@atlassian/jira-business-constants';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useQueryParam } from '@atlassian/react-resource-router';

export type CommentsSidebarProps = {
	issueKey: IssueKey;
	onClose: () => void;
};

export const useCommentsSidebar = ({
	CommentsSidebar,
}: {
	CommentsSidebar: React.FC<CommentsSidebarProps>;
}) => {
	const [selectedIssueKey, setSelectedIssueKey] = useQueryParam(SELECTED_COMMENT_PARAM);

	const [, { setSidebarContent }] = useSidebar();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// set query param when comment cell is clicked
	const onOpen = useCallback(
		(issueKey: string) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'commentsSidebar opened');
			setSelectedIssueKey(issueKey);
		},
		[createAnalyticsEvent, setSelectedIssueKey],
	);

	// close comment sidebar on [X] button click
	const onClose = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'commentsSidebar closed');
		setSelectedIssueKey(undefined);
		setSidebarContent(null);
	}, [createAnalyticsEvent, setSelectedIssueKey, setSidebarContent]);

	// set sidebar content after query param is set
	useEffect(() => {
		if (selectedIssueKey) {
			const newContent = <CommentsSidebar issueKey={selectedIssueKey} onClose={onClose} />;
			setSidebarContent(newContent);
		}
	}, [CommentsSidebar, onClose, selectedIssueKey, setSidebarContent]);

	return {
		selectedIssueKey,
		onOpenCommentsSidebar: onOpen,
		onCloseCommentsSidebar: onClose,
	};
};
